import React, { useRef } from "react";
import { Box, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ResponsiveBar } from "@nivo/bar";
import HyroChartTooltip from "@hyro-components/hyro-chart-tooltip/HyroChartTooltip";
import { formatTick, niceTickStep, roundedTopRectPath } from "@hyro-components/hyro-bar-chart/HyroBarChart.utils";
import palette from "src/theme/palette";
import typography from "src/theme/typography";
import { STACKED_BAR_DEFAULTS, TOOLTIP_CARD_STYLE } from "./HyroStackedBarChart.consts";

const TooltipCard = styled("div")(({ theme }) => ({
  ...TOOLTIP_CARD_STYLE,
  backgroundColor: palette.system[0],
  boxShadow: theme.shadows[2],
  boxSizing: "border-box"
}));

// Bars are a fixed width centered in nivo's band, so columns stay evenly distributed at any container width.
const barGeometry = (bar, barWidth) => {
  const width = Math.min(barWidth, bar.width);
  return { x: bar.x + ((bar.width - width) / 2), width };
};

// Topmost non-zero segment of each column gets the rounded corners; stacking order = keys order (bottom → top).
const topSegmentByLabel = (data, keys) =>
  Object.fromEntries(data.map((row) => [row.label, [...keys].reverse().find((key) => row[key] > 0) ?? null]));

// Stacked bar chart: one column per `data` row (`row.label` on the x axis), one segment per key in `keys`
// (bottom → top). Hovering a segment opens `renderTooltip({ key, row })` in a light tooltip card.
const HyroStackedBarChart = ({
  data = [],
  keys = [],
  getColor,
  renderTooltip,
  height = STACKED_BAR_DEFAULTS.height,
  barWidth = STACKED_BAR_DEFAULTS.barWidth,
  loading = false,
  testId = "hyro-stacked-bar-chart"
}) => {
  const hoverTooltipRef = useRef(null);
  const chartRef = useRef(null);

  if (loading) {
    return (
      <Box sx={{ height, width: "100%" }} data-testid={`${testId}-skeleton`}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  const topSegments = topSegmentByLabel(data, keys);
  const columnTotal = (row) => keys.reduce((sum, key) => sum + (row[key] || 0), 0);
  const dataMax = Math.max(0, ...data.map(columnTotal));
  const step = niceTickStep(dataMax);
  const yTickValues = Array.from({ length: STACKED_BAR_DEFAULTS.yTickCount + 1 }, (_, i) => step * i);

  const StackedSegment = ({ bar }) => {
    if (!(bar.data.value > 0)) return null;
    const { x, width } = barGeometry(bar, barWidth);
    const isTop = topSegments[bar.data.indexValue] === bar.data.id;
    return isTop
      ? <path d={roundedTopRectPath(x, bar.y, width, bar.height, STACKED_BAR_DEFAULTS.barRadius)} fill={bar.color} />
      : <rect x={x} y={bar.y} width={width} height={bar.height} fill={bar.color} />;
  };

  const showTooltip = (event, bar) => {
    const row = data.find((d) => d.label === bar.data.indexValue);
    hoverTooltipRef.current?.show(
      { key: bar.data.id, row },
      event.currentTarget.getBoundingClientRect(),
      // the chart element, not the hovered <rect>: popper sizes a boundary from
      // clientWidth/clientHeight, which are always 0 on an SVG child
      { boundary: chartRef.current }
    );
  };

  // Transparent hit areas per segment, rendered on top so nivo's own bars never re-render on hover.
  const InteractionLayer = ({ bars }) => (
    <g>
      {bars.filter((bar) => bar.data.value > 0).map((bar) => {
        const { x, width } = barGeometry(bar, barWidth);
        return (
          <rect
            key={`hover-${bar.data.indexValue}-${bar.data.id}`}
            data-testid={`${testId}-bar-${bar.data.indexValue}-${bar.data.id}`}
            x={x}
            y={bar.y}
            width={width}
            height={bar.height}
            fill="transparent"
            style={{ pointerEvents: "all" }}
            onMouseEnter={(event) => showTooltip(event, bar)}
            onMouseLeave={() => hoverTooltipRef.current?.hide()}
          />
        );
      })}
    </g>
  );

  const renderXTick = ({ x, y, value }) => (
    <g transform={`translate(${x},${y + STACKED_BAR_DEFAULTS.xLabelOffset})`}>
      <text
        textAnchor="middle"
        dominantBaseline="hanging"
        style={{ ...typography.subtitle2, letterSpacing: "0.1px", fill: palette.text[800] }}
      >
        {value}
      </text>
    </g>
  );

  const tooltipModifiers = ({ boundary }) => [
    { name: "offset", options: { offset: [0, STACKED_BAR_DEFAULTS.tooltipOffset] } },
    { name: "flip", options: { fallbackPlacements: ["left", "top"], boundary } },
    { name: "preventOverflow", options: { boundary } }
  ];

  return (
    <Box ref={chartRef} sx={{ height, width: "100%" }} data-testid={testId}>
      <ResponsiveBar
        data={data}
        keys={keys}
        indexBy="label"
        groupMode="stacked"
        margin={STACKED_BAR_DEFAULTS.margin}
        padding={0.3}
        valueScale={{ type: "linear", min: 0, max: step * STACKED_BAR_DEFAULTS.yTickCount }}
        indexScale={{ type: "band", round: true }}
        colors={(bar) => getColor(bar.id)}
        barComponent={StackedSegment}
        enableLabel={false}
        axisTop={null}
        axisRight={null}
        axisBottom={{ tickSize: 0, tickPadding: 0, renderTick: renderXTick }}
        axisLeft={{ tickSize: 0, tickPadding: 18, tickValues: yTickValues, format: formatTick }}
        enableGridY
        gridYValues={yTickValues}
        enableGridX={false}
        layers={["grid", "axes", "bars", InteractionLayer]}
        isInteractive={false}
        theme={{
          axis: {
            ticks: {
              text: {
                fill: palette.text[700],
                fontSize: STACKED_BAR_DEFAULTS.axisFontSize,
                letterSpacing: STACKED_BAR_DEFAULTS.axisLetterSpacing,
                fontFamily: typography.fontFamily
              }
            }
          },
          grid: { line: { stroke: palette.system[200], strokeWidth: 1, strokeDasharray: "0" } }
        }}
        animate
        motionConfig="gentle"
      />
      {renderTooltip && (
        <HyroChartTooltip
          ref={hoverTooltipRef}
          placement="right"
          modifiers={tooltipModifiers}
          renderContent={(datum) => <TooltipCard>{renderTooltip(datum)}</TooltipCard>}
        />
      )}
    </Box>
  );
};

export default HyroStackedBarChart;
